import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridEventListener,
  GridPaginationModel,
  GridPagination,
} from "@mui/x-data-grid";
import { Collector } from "../../services/shirts/types";
import FormatService from "../../services/formatService";
import { COLLECTION_TAB } from "../MyProfile/MyProfile";
import ProfileImg from "../../components/ProfileImg/ProfileImg";
import { COLLECTION_SECTION } from "../MyProfile/Collection/Collection";
import { AbilityContext, Can } from "../../permission/Can";
import { Actions, Subjects } from "../../permission/constants";
import { Collection as CollectionEntity } from "../../entities/Collection";
import { COUNTRIES } from "../../constants/countries";
import PremiumBadgeIcon from "../../img/icons/vip.png";
import { RootState } from "../../state";
import { roles } from "../../types/authTypes";
import RedirectService from "../../services/redirectService";

const { getMoneyFormat } = FormatService();

const columns: GridColDef[] = [
  {
    field: "rowId",
    headerName: "",
    display: "flex",
    filterable: false,
    renderCell: (params) =>
      params.api.getAllRowIds().indexOf(params.id) +
      1 +
      (params.api.state.pagination.paginationModel.page - 1) *
        params.api.state.pagination.paginationModel.pageSize,
    headerClassName: "collectors-header-first",
    width: 50,
    align: "center",
  },
  {
    field: "user_name",
    renderCell: (params) => {
      return (
        <Box
          sx={{
            display: "flex",
            columnGap: 1,
            alignItems: "center",
          }}
        >
          <Box>
            <ProfileImg imgUrl={params.row.profile_image?.url} />
          </Box>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: "600 !important" }}>
              {params.row.user_name}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap="1">
            {params.row.is_vip && (
              <Box sx={{ width: "25px" }}>
                <ProfileImg imgUrl={PremiumBadgeIcon} size={20} hideShadow />
              </Box>
            )}
            {params.row.country && (
              <Box sx={{ width: "20px" }}>
                <Flag countryLabel={params.row.country} />
              </Box>
            )}
          </Box>
        </Box>
      );
    },
    headerName: "User Name",
    headerClassName: "collectors-header",
    width: 300,
  },
  {
    field: "item_count",
    headerName: "Number of shirts",
    valueGetter: (value) => {
      return value ? value + " Shirts" : "No shirts yet";
    },
    headerClassName: "collectors-header",
    width: 200,
    display: "flex",
    align: "left",
  },
  {
    field: "biggest_club_collection",
    display: "flex",
    headerName: "Biggest club in collection",
    renderCell: (params) => {
      return params.row.biggest_club_collection_name
        ? params.row.biggest_club_collection_count +
            " " +
            params.row.biggest_club_collection_name +
            " Shirts"
        : "No shirts yet";
    },
    width: 330,
    headerClassName: "collectors-header",
  },
  {
    field: "match_worn_item_count",
    display: "flex",
    headerName: "Matchworn shirts",
    valueGetter: (value) => {
      return value + " Matchworn";
    },
    width: 250,
    headerClassName: "collectors-header",
  },
  // {
  //   field: "followers",
  //   display: "flex",
  //   headerName: "Followers",
  //   valueGetter: (value) => {
  //     return value + " Followers";
  //   },
  //   width: 150,
  //   headerClassName: "collectors-header",
  // },
  // {
  //   field: "following",
  //   display: "flex",
  //   headerName: "Following",
  //   valueGetter: (value) => {
  //     return value + " Following";
  //   },
  //   width: 150,
  //   headerClassName: "collectors-header-last",
  // },
];

const columnsPremium: GridColDef[] = [
  {
    field: "rowId",
    display: "flex",
    headerName: "",
    filterable: false,
    renderCell: (params) =>
      params.api.getAllRowIds().indexOf(params.id) +
      1 +
      (params.api.state.pagination.paginationModel.page - 1) *
        params.api.state.pagination.paginationModel.pageSize,
    width: 50,
    headerClassName: "collectors-header-first",
  },
  {
    field: "user_name",
    headerName: "User Name",
    display: "flex",
    renderCell: (params) => {
      return (
        <Box
          sx={{
            display: "flex",
            columnGap: 1,
            alignItems: "center",
          }}
        >
          <Box>
            <ProfileImg imgUrl={params.row.profile_image?.url} hideShadow />
          </Box>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: "600 !important" }}>
              {params.row.user_name}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap="1">
            {params.row.is_vip && (
              <Box sx={{ width: "25px" }}>
                <ProfileImg imgUrl={PremiumBadgeIcon} size={20} />
              </Box>
            )}
            {params.row.country && (
              <Box sx={{ width: "20px" }}>
                <Flag countryLabel={params.row.country} />
              </Box>
            )}
          </Box>
        </Box>
      );
    },
    width: 300,
    headerClassName: "collectors-header",
  },
  {
    field: "item_count",
    display: "flex",
    headerName: "Number of shirts",
    valueGetter: (value) => {
      return value ? value + " Shirts" : "No shirts yet";
    },
    width: 200,
    headerClassName: "collectors-header",
  },
  {
    field: "biggest_club_collection",
    display: "flex",
    headerName: "Biggest club in collection",
    renderCell: (params) => {
      return params.row.biggest_club_collection_name
        ? params.row.biggest_club_collection_count +
            " " +
            params.row.biggest_club_collection_name +
            " Shirts"
        : "No shirts yet";
    },
    width: 330,
    headerClassName: "collectors-header",
  },
  {
    field: "match_worn_item_count",
    display: "flex",
    headerName: "Matchworn shirts",
    valueGetter: (value) => {
      return value + " Matchworn";
    },
    width: 250,
    headerClassName: "collectors-header",
  },
  // {
  //   field: "collection_value",
  //   display: "flex",
  //   headerName: "Value of collection",
  //   renderCell: (params) => {
  //     const collectionInstance = new CollectionEntity(params.row.id);
  //     return (
  //       <Can I={Actions.VIEW_CURRENT_VALUE} this={collectionInstance}>
  //         {getMoneyFormat(params.row.collection_value)}
  //       </Can>
  //     );
  //   },
  //   width: 200,
  //   headerClassName: "collectors-header",
  // },
  // {
  //   field: "followers",
  //   display: "flex",
  //   headerName: "Followers",
  //   valueGetter: (value) => {
  //     return value + " Followers";
  //   },
  //   width: 150,
  //   headerClassName: "collectors-header",
  // },
  // {
  //   field: "following",
  //   display: "flex",
  //   headerName: "Following",
  //   valueGetter: (value) => {
  //     return value + " Following";
  //   },
  //   width: 150,
  //   headerClassName: "collectors-header-last",
  // },
];

interface CollectorsResultProps {
  rows: Array<Collector> | undefined;
  totalPageCount: number | undefined;
  paginationModel: GridPaginationModel;
  syncPaginationModel: (value: GridPaginationModel) => void;
}

const itemsPerPageOptions = [10, 20, 50];

const CollectorsResult = ({
  rows,
  totalPageCount,
  paginationModel,
  syncPaginationModel,
}: CollectorsResultProps) => {
  const navigate = useNavigate();
  const ability = useContext(AbilityContext);
  const loginData = useSelector((state: RootState) => state.login);
  const { setRedirectUrl } = RedirectService();

  const handleRowClick: GridEventListener<"rowClick"> = (params) => {
    const targetUrl =
      "/my-profile/" +
      params.row.id +
      "/" +
      COLLECTION_TAB.COLLECTION +
      "/" +
      COLLECTION_SECTION.OVERVIEW;

    // Check if user is visitor
    if (loginData.role === roles.VISITOR) {
      // Store the intended URL for redirect after login
      setRedirectUrl(targetUrl);
      // Redirect to sign-up page
      window.location.href = "/sign-up";
    } else {
      // User is logged in, navigate normally
      navigate(targetUrl);
    }
  };

  const [rowCount, setRowCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    itemsPerPageOptions[0]
  );

  useEffect(() => {
    syncPaginationModel({ ...paginationModel, pageSize: itemsPerPage });
  }, [itemsPerPage]);

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#fff",
        borderRadius: "10px",
        padding: "0.5rem",
      }}
    >
      <Box
        sx={{
          height: 1000,
          width: "100%",
          border: "none",
          "& .collectors-header-first": {
            backgroundColor: "#4663ff",
            color: "white",
            fontWeight: 600,
            fontFamily: "Manrope",
            borderRadius: "10px 0px 0px 10px !important",
          },
          "& .collectors-header": {
            backgroundColor: "#4663ff",
            color: "white",
            fontWeight: 600,
            fontFamily: "Manrope",
          },
          "& .collectors-header-last": {
            backgroundColor: "#4663ff",
            color: "white",
            fontWeight: 600,
            fontFamily: "Manrope",
            borderRadius: "0px 10px 10px 0px !important",
          },
        }}
      >
        <DataGrid
          rows={rows}
          columns={
            ability.can(Actions.VIEW_CURRENT_VALUE, Subjects.COLLECTION)
              ? columnsPremium
              : columns
          }
          rowCount={rowCount}
          onRowClick={handleRowClick}
          getRowHeight={() => "auto"}
          sx={{
            border: "none",
            "& .MuiDataGrid-columnSeparator": {
              display: "none",
            },
            "& .MuiDataGrid-cell": {
              padding: "20px 10px",
              height: "85px",
              fontWeight: 600,
            },
          }}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={syncPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          slots={{
            footer: () => (
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                padding={2}
              >
                <FormControl
                  fullWidth
                  sx={{
                    width: 60,
                    marginRight: "20px",
                    marginBottom: { xs: "10px", md: "0px" },
                  }}
                  size="small"
                  variant="standard"
                >
                  <Select
                    labelId="items-per-page-label"
                    value={itemsPerPage}
                    label="Items Per Page"
                    onChange={(event) =>
                      setItemsPerPage &&
                      setItemsPerPage(event.target.value as number)
                    }
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          "& .MuiMenuItem-root": {
                            fontSize: "16px !important",
                          },
                        },
                      },
                    }}
                  >
                    {itemsPerPageOptions.map((item, index) => (
                      <MenuItem
                        value={item}
                        key={`page-controller-footer-${index}`}
                      >
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  onClick={() => {
                    syncPaginationModel({
                      page: paginationModel.page - 1,
                      pageSize: paginationModel.pageSize,
                    });
                  }}
                  disabled={paginationModel.page === 1}
                >
                  ◀
                </Button>
                <Typography sx={{ marginBottom: "3px" }}>
                  Page {paginationModel.page} of {totalPageCount}
                </Typography>
                <Button
                  onClick={() => {
                    syncPaginationModel({
                      page: paginationModel.page + 1,
                      pageSize: paginationModel.pageSize,
                    });
                  }}
                  disabled={paginationModel.page === totalPageCount}
                >
                  ▶
                </Button>
              </Box>
            ),
          }}
        />
      </Box>
    </Box>
  );
};

interface FlagProps {
  countryLabel: string | undefined;
}

export const Flag = ({ countryLabel }: FlagProps) => {
  const getCodeFromLabel = (
    countryLabel: string | undefined
  ): string | undefined => {
    if (countryLabel === undefined) {
      return undefined;
    }
    const normalizedLabel = countryLabel.toLowerCase(); // Convertimos a minúsculas para comparar sin importar mayúsculas

    const foundCountry = COUNTRIES.find(
      (country) => country.label.toLowerCase() === normalizedLabel
    );

    return foundCountry ? foundCountry.code : undefined;
  };

  let flag = <></>;
  const code = getCodeFromLabel(countryLabel);
  if (code) {
    flag = (
      <ProfileImg
        imgUrl={`https://flagcdn.com/w40/${code.toLowerCase()}.png`}
        size={16}
        hideShadow
      />
    );
  }
  return flag;
};

export default CollectorsResult;
